import { OperatorElearningTopicTranslationService } from '../../../../../api/api-services/operator-elearning-topic-translation.service';
import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { OperatorElearningTopicsService } from '../../../../../api/api-services/operator-elearning-topics.service';
import { IOperatorElearningTopicTranslationIm } from '../../../../../api/operator-elearning-topic-translation-im.model';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { OperatorElearningTopicService } from '../../../../../api/api-services/operator-elearning-topic.service';
import { IOperatorElearningTopicIm } from '../../../../../api/operator-elearning-topic-im.model';
import { IOperatorElearningTopicVm } from '../../../../../api/operator-elearning-topic-vm.model';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { environment } from '../../../../../environments/environment';
import { eModalRoles } from '../../../../shared/models/modals';
import { Component, OnInit, inject } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

interface UploadFileResultVm {
  publicVisible: boolean;
  success: boolean;
  size: number;
  fileUrl: string;
}

@Component({
  selector: 'app-topic-create',
  templateUrl: './topic-create.component.html',
  styleUrl: './topic-create.component.scss'
})
export class TopicCreateComponent implements OnInit {

  private _operatorElearningTopicCategoriesService = inject(OperatorElearningTopicCategoriesService);
  private _operatorElearningTopicsService = inject(OperatorElearningTopicsService);
  private _translationService = inject(OperatorElearningTopicTranslationService);
  private _topicService = inject(OperatorElearningTopicService);
  private _messageService = inject(MessageService);
  private _dialogRef = inject(DynamicDialogRef);
  private _http = inject(HttpClient);
  private _fb = inject(FormBuilder);

  public selectedImageLanguages: string[] = [];

  public showAddImageLanguageDropdown = false;
  public selectedNewImageLanguage = '';
  public environment = environment;
  public maxTranslations = 3;
  public currentStep = 1;
  public loading = false;
  
  public uploadedImages: { [languageCode: string]: string } = {};
  public categories: IOperatorElearningTopicCategoryVm[] = [];
  public selectedFiles: { [languageCode: string]: File } = {};
  public topicForm!: FormGroup;

  private imageLanguageCollapseState: { [key: string]: boolean } = {};
  
  public translationLanguages = [
    { code: 'pl', name: 'Polski' },
    { code: 'en', name: 'Angielski' },
    { code: 'uk', name: 'Ukraiński' },
    { code: 'de', name: 'Niemiecki' }
  ];

  ngOnInit(): void {
    this.initForm();
    this.loadCategories();
  }

  private initForm(): void {
    this.topicForm = this._fb.group({
      categoryId: [null, [Validators.required]],
      name: ['', [Validators.required, Validators.minLength(3)]],
      order: [null, [Validators.required, Validators.min(0)]],
      translations: this._fb.array([])
    });
  }

  private loadCategories(): void {
    this.loading = true;
    this._operatorElearningTopicCategoriesService.get().subscribe({
      next: (categories) => {
        this.categories = categories;
        this.loading = false;
      },
      error: (error) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się załadować działów'
        });
        this.loading = false;
      }
    });
  }

  get translations(): FormArray {
    return this.topicForm.get('translations') as FormArray;
  }

  public onCategoryChange(categoryId: number): void {
    if (categoryId) {
      this.updateOrderBasedOnCategory(categoryId);
    }
  }

  private updateOrderBasedOnCategory(categoryId: number): void {
    this._operatorElearningTopicsService.get(categoryId).subscribe({
      next: (topics) => {
        const maxOrder = topics.length > 0
          ? Math.max(...topics.map(t => t.order || 0))
          : 0;
        const nextOrder = maxOrder + 1;
        this.topicForm.patchValue({ order: nextOrder });
      },
      error: (error) => {
        this.topicForm.patchValue({ order: 1 });
      }
    });
  }

  public addNewTranslationForm(): void {
    if (this.translations.length < this.maxTranslations) {
      const translationGroup = this._fb.group({
        languageCode: ['', [Validators.required]],
        name: ['', [Validators.required, Validators.minLength(2)]],
        collapsed: [false]
      });
      this.translations.push(translationGroup);
    }
  }

  public removeTranslationForm(index: number): void {
    this.translations.removeAt(index);
  }

  public toggleTranslationCollapse(index: number): void {
    const translation = this.translations.at(index);
    if (translation) {
      const currentCollapsed = translation.get('collapsed')?.value;
      translation.get('collapsed')?.setValue(!currentCollapsed);
    }
  }

  public isTranslationCollapsed(index: number): boolean {
    const translation = this.translations.at(index);
    return translation ? translation.get('collapsed')?.value : false;
  }

  public canAddMoreTranslations(): boolean {
    return this.translations.length < this.maxTranslations;
  }

  public addTranslation(languageCode: string): void {
    const translationGroup = this._fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      languageCode: [languageCode, [Validators.required]]
    });
    this.translations.push(translationGroup);
  }

  public removeTranslation(index: number): void {
    this.translations.removeAt(index);
  }

  public nextStep(): void {
    if (this.currentStep === 1 && this.isStep1Valid()) {
      this.currentStep = 2;
    }
  }

  public previousStep(): void {
    if (this.currentStep === 2) {
      this.currentStep = 1;
    }
  }

  private isStep1Valid(): boolean {
    const categoryId = this.topicForm.get('categoryId')?.value;
    const name = this.topicForm.get('name')?.value;
    const order = this.topicForm.get('order')?.value;
    
    if (!categoryId || !name || name.length < 3 || order === null || order === undefined) {
      return false;
    }

    for (let i = 0; i < this.translations.length; i++) {
      const translation = this.translations.at(i);
      const languageCode = translation.get('languageCode')?.value;
      const translationName = translation.get('name')?.value;

      if (languageCode && (!translationName || translationName.trim().length < 2)) {
        return false;
      }
    }

    return true;
  }

  public isStep1ValidPublic(): boolean {
    const categoryValid = this.topicForm.get('categoryId')?.valid;
    const nameValid = this.topicForm.get('name')?.valid;
    const orderValid = this.topicForm.get('order')?.valid;
    const translationsValid = this.translations.valid;
    
    return !!categoryValid && !!nameValid && !!orderValid && translationsValid;
  }

  public onFileSelected(files: File | File[], languageCode: string = 'pl'): void {
    if (files instanceof File) {
      this.selectedFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name} dla języka ${this.getLanguageName(languageCode)}`
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files[0].name} dla języka ${this.getLanguageName(languageCode)}`
      });
    }
  }

  private async uploadImageToServer(file: File, languageCode: string): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');
      
      const response = await firstValueFrom(
        this._http.post<UploadFileResultVm>(`${environment.webApiUrl}/UploadMedia`, formData)
      );

      if (response.success) {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: `Zdjęcie dla języka ${this.getLanguageName(languageCode)} zostało przesłane.`
        });
        return response.fileUrl;
      } else {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się przesłać zdjęcia.'
        });
        throw new Error('Upload failed');
      }
    } catch (error) {
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Wystąpił błąd podczas przesyłania zdjęcia.'
      });
      throw error;
    }
  }

  public async saveTopic(): Promise<void> {
    if (this.topicForm.valid && this.areAllImagesUploaded()) {
      this.loading = true;
      
      try {
        await this.uploadAllImages();
        const categoryId = this.topicForm.get('categoryId')?.value;
        const topicData: IOperatorElearningTopicIm = {
          name: this.topicForm.get('name')?.value,
          order: this.topicForm.get('order')?.value,
          thumbnailUrl: this.uploadedImages['pl'] || '',
          elearningTopicCategoryId: categoryId
        };

        this._topicService.post(topicData).subscribe({
          next: (newTopic) => {
            const translations = this.translations.value;
            if (translations.length > 0) {
              this.saveTranslations(newTopic.id, translations, newTopic);
            } else {
              this.completeTopicCreation(newTopic);
            }
          },
          error: (error) => {
            console.error('Błąd tworzenia rozdziału:', error);
            this._messageService.add({
              severity: 'error',
              summary: 'Błąd',
              detail: 'Nie udało się utworzyć nowego rozdziału'
            });
            this.loading = false;
          }
        });
        
      } catch (error) {
        console.error('Błąd przesyłania plików:', error);
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się przesłać plików'
        });
        this.loading = false;
      }
    } else if (!this.areAllImagesUploaded()) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać wszystkie wymagane pliki przed zapisaniem rozdziału.'
      });
    }
  }

  private async uploadAllImages(): Promise<void> {
    if (this.selectedFiles['pl']) {
      const url = await this.uploadImageToServer(this.selectedFiles['pl'], 'pl');
      this.uploadedImages['pl'] = url;
    }

    for (const languageCode of this.selectedImageLanguages) {
      if (this.selectedFiles[languageCode]) {
        const url = await this.uploadImageToServer(this.selectedFiles[languageCode], languageCode);
        this.uploadedImages[languageCode] = url;
      }
    }
  }

  public areAllImagesUploaded(): boolean {
    
    if (!this.selectedFiles['pl']) {
      return false;
    }

    for (const languageCode of this.selectedImageLanguages) {
      if (!this.selectedFiles[languageCode]) {
        return false;
      }
    }
    return true;
  }

  private saveTranslations(topicId: number, translations: any[], topic: IOperatorElearningTopicVm): void {
    let savedCount = 0;
    const totalTranslations = translations.length;

    translations.forEach((translation) => {
      const translationData: IOperatorElearningTopicTranslationIm = {
        elearningTopicId: topicId,
        name: translation.name,
        thumbnailUrl: this.selectedImageLanguages.includes(translation.languageCode) 
          ? this.uploadedImages[translation.languageCode] || '' 
          : '',
        languageCode: translation.languageCode
      };

      this._translationService.post(translationData).subscribe({
        next: () => {
          savedCount++;
          if (savedCount === totalTranslations) {
            this.completeTopicCreation(topic);
          }
        },
        error: (error) => {
          console.error('Błąd zapisywania tłumaczenia:', error);
          savedCount++;
          if (savedCount === totalTranslations) {
            this.completeTopicCreation(topic);
          }
        }
      });
    });
  }

  private completeTopicCreation(topic: IOperatorElearningTopicVm): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Nowy rozdział został dodany'
    });
    this.closeDialog(eModalRoles.SUCCESS);
  }

  public closeDialog(status: eModalRoles = eModalRoles.CLOSE): void {
    this._dialogRef.close(status);
  }

  public getLanguageName(languageCode: string): string {
    const language = this.translationLanguages.find(lang => lang.code === languageCode);
    return language ? language.name : languageCode;
  }

  public getAvailableLanguagesForTranslation(currentIndex?: number) {
    const used = this.translations.controls
      .map((control, idx) => idx === currentIndex ? null : control.get('languageCode')?.value)
      .filter((code): code is string => !!code);

    return this.translationLanguages.filter(lang =>
      lang.code !== 'pl' && !used.includes(lang.code)
    );
  }

  public getSelectedImageLanguages(): string[] {
    return this.selectedImageLanguages;
  }


  public getLanguagesWithBasicData(): string[] {
    return this.translations.controls
      .filter(control => {
        const languageCode = control.get('languageCode')?.value;
        const name = control.get('name')?.value;
        return languageCode && name && name.trim().length >= 2; // minimalna długość nazwy
      })
      .map(control => control.get('languageCode')?.value)
      .filter((code): code is string => !!code);
  }

  public canAddMediaForLanguage(languageCode: string): boolean {
    return this.getLanguagesWithBasicData().includes(languageCode);
  }

  public getAvailableLanguagesForImages() {
    const languagesWithBasicData = this.getLanguagesWithBasicData();
    return this.translationLanguages.filter(lang => 
      lang.code !== 'pl' && 
      !this.selectedImageLanguages.includes(lang.code) &&
      languagesWithBasicData.includes(lang.code)
    );
  }

  public addImageLanguage(languageCode: string): void {
    if (languageCode && !this.selectedImageLanguages.includes(languageCode)) {
      if (!this.canAddMediaForLanguage(languageCode)) {
        this._messageService.add({
          severity: 'warn',
          summary: 'Uwaga',
          detail: `Nie można dodać mediów dla języka ${this.getLanguageName(languageCode)}. Najpierw uzupełnij nazwę rozdziału w tym języku.`
        });
        return;
      }
      this.selectedImageLanguages.push(languageCode);
    }
  }

  public removeImageLanguage(languageCode: string): void {
    const index = this.selectedImageLanguages.indexOf(languageCode);
    if (index > -1) {
      this.selectedImageLanguages.splice(index, 1);
      delete this.selectedFiles[languageCode];
      delete this.uploadedImages[languageCode];
      delete this.imageLanguageCollapseState[languageCode];
    }
  }

  public toggleImageLanguageCollapse(languageCode: string): void {
    this.imageLanguageCollapseState[languageCode] = !this.imageLanguageCollapseState[languageCode];
  }

  public isImageLanguageCollapsed(languageCode: string): boolean {
    return this.imageLanguageCollapseState[languageCode] || false;
  }

  public canAddMoreImageLanguages(): boolean {
    return this.getAvailableLanguagesForImages().length > 0;
  }

  public onNewImageLanguageSelected(): void {
    if (this.selectedNewImageLanguage) {
      this.addImageLanguage(this.selectedNewImageLanguage);
      this.selectedNewImageLanguage = '';
      this.showAddImageLanguageDropdown = false;
    }
  }
}
